import { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const FILTERS = [
  { key: 'all', label: 'All Stories' },
  { key: 'written', label: 'Written' },
  { key: 'photo', label: 'Photo Stories' },
  { key: 'video', label: 'Video Interviews' },
];

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ name: '', country: '', quoteText: '', rating: 5 });
  const [mediaFile, setMediaFile] = useState(null);
  const [submitStatus, setSubmitStatus] = useState('idle');

  const fetchTestimonials = () => {
    const query = filter === 'all' ? '' : `?type=${filter}`;
    fetch(`${API_BASE}/testimonials${query}`)
      .then((r) => r.json())
      .then((json) => setTestimonials(json.success ? json.data : []));
  };

  useEffect(fetchTestimonials, [filter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('sending');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => fd.append(key, value));
      if (mediaFile) fd.append('media', mediaFile);

      const res = await fetch(`${API_BASE}/testimonials`, { method: 'POST', body: fd });
      const json = await res.json();
      setSubmitStatus(json.success ? 'sent' : 'error');
      if (json.success) {
        setForm({ name: '', country: '', quoteText: '', rating: 5 });
        setMediaFile(null);
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-20">
        <header className="pt-20 pb-20 px-6 bg-[#f7f9fb] text-center">
          <div className="max-w-[1280px] mx-auto">
            <h1 className="text-5xl font-bold text-[#00255a] mb-6">Study Abroad Success Stories</h1>
            <p className="text-lg text-[#44474f] max-w-2xl mx-auto">
              Discover the journeys of international students and professionals who achieved their global dreams with IASC Consultancy.
            </p>
          </div>
        </header>

        <div className="max-w-[1280px] mx-auto px-6 pb-32">
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-8 py-3 rounded-full font-semibold text-sm transition-all ${
                  filter === f.key ? 'text-white shadow-md' : 'bg-[#e6e8ea] text-[#191c1e] hover:bg-[#00255a]/10'
                }`}
                style={filter === f.key ? { background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' } : {}}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Grid */}
          {testimonials.length === 0 ? (
            <p className="text-center text-[#64748b] py-12">No testimonials to show yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((t) => (
                <TestimonialCard key={t.id} testimonial={t} />
              ))}
            </div>
          )}

          {/* Share Your Story */}
          <section className="mt-32 pt-24 border-t border-[#e2e8f0]" id="share-story">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#00255a] mb-4">Share Your Story</h2>
                <p className="text-[#44474f]">Inspired by others? Help future applicants by sharing your own IASC journey.</p>
              </div>

              <div className="bg-white rounded-3xl p-8 md:p-12 border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)]">
                {submitStatus === 'sent' ? (
                  <div className="text-center py-12">
                    <span className="material-symbols-outlined text-6xl text-green-600 mb-4">check_circle</span>
                    <h3 className="text-xl font-bold text-[#00255a] mb-2">Thank you for sharing!</h3>
                    <p className="text-[#64748b]">Your story is under review and will be published once approved.</p>
                  </div>
                ) : (
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    {submitStatus === 'error' && (
                      <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                        Something went wrong. Please try again.
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-[#191c1e]">Full Name</label>
                        <input
                          required
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="e.g. John Doe"
                          className="w-full px-4 py-3 rounded-xl border border-[#e2e8f0] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-[#191c1e]">Country or Program</label>
                        <input
                          value={form.country}
                          onChange={(e) => setForm({ ...form, country: e.target.value })}
                          placeholder="e.g. MBA, Canada"
                          className="w-full px-4 py-3 rounded-xl border border-[#e2e8f0] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-[#191c1e]">Your Testimonial</label>
                      <textarea
                        required
                        rows="4"
                        value={form.quoteText}
                        onChange={(e) => setForm({ ...form, quoteText: e.target.value })}
                        placeholder="How was your experience with IASC Consultancy?"
                        className="w-full px-4 py-3 rounded-xl border border-[#e2e8f0] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-[#191c1e] block mb-2">Star Rating</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setForm({ ...form, rating: star })}
                            className="material-symbols-outlined text-3xl"
                            style={{
                              fontVariationSettings: "'FILL' 1",
                              color: star <= form.rating ? '#fbbf24' : '#e0e3e5',
                            }}
                          >
                            star
                          </button>
                        ))}
                      </div>
                    </div>
                    <label className="p-8 border-2 border-dashed border-[#e2e8f0] rounded-2xl bg-[#f7f9fb] flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-[#f2f4f6] transition-all">
                      <span className="material-symbols-outlined text-4xl text-[#44474f]">cloud_upload</span>
                      <p className="font-semibold text-[#44474f]">
                        {mediaFile ? mediaFile.name : 'Optional: Upload Photo or Video'}
                      </p>
                      <p className="text-[12px] text-[#747781]">Max size: 100MB. Formats: JPG, PNG, MP4</p>
                      <input type="file" className="hidden" accept="image/*,video/*" onChange={(e) => setMediaFile(e.target.files[0])} />
                    </label>
                    <div className="flex flex-col items-center gap-6">
                      <button
                        type="submit"
                        disabled={submitStatus === 'sending'}
                        className="w-full md:w-auto px-12 py-4 rounded-xl font-semibold text-white shadow-xl hover:scale-[1.02] transition-all disabled:opacity-60"
                        style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
                      >
                        {submitStatus === 'sending' ? 'Submitting...' : 'Submit Your Story'}
                      </button>
                      <p className="text-[12px] text-[#44474f] flex items-center gap-2 italic">
                        <span className="material-symbols-outlined text-sm">info</span>
                        All testimonials are reviewed before publishing to ensure authenticity.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function TestimonialCard({ testimonial: t }) {
  const isVideo = t.mediaType === 'VIDEO';
  const isPhoto = t.mediaType === 'PHOTO';

  if (isVideo) {
    return (
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all flex flex-col group">
        <div className="relative h-64 cursor-pointer overflow-hidden">
          <img className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" src={t.mediaUrl} alt={t.name} />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/50 group-hover:bg-white transition-all">
              <span className="material-symbols-outlined text-white group-hover:text-[#00255a]">play_arrow</span>
            </div>
          </div>
        </div>
        <div className="p-8">
          <Stars rating={t.rating} />
          <p className="text-[#191c1e] mb-6 italic mt-4">&quot;{t.quoteText}&quot;</p>
          <Attribution name={t.name} country={t.country} />
        </div>
      </div>
    );
  }

  if (isPhoto) {
    return (
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all flex flex-col">
        <div className="relative h-80 overflow-hidden">
          <img className="w-full h-full object-cover" src={t.mediaUrl} alt={t.name} />
        </div>
        <div className="p-8">
          <Stars rating={t.rating} />
          <p className="text-[#44474f] mb-6 mt-4">{t.quoteText}</p>
          <Attribution name={t.name} country={t.country} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 border border-[#e2e8f0] shadow-sm hover:shadow-lg transition-all flex flex-col justify-between">
      <div>
        <div className="flex justify-between items-start mb-6">
          <span className="material-symbols-outlined text-[#00255a]/20 text-5xl">format_quote</span>
          <Stars rating={t.rating} />
        </div>
        <p className="text-[#191c1e] mb-8 leading-relaxed">{t.quoteText}</p>
      </div>
      <Attribution name={t.name} country={t.country} />
    </div>
  );
}

function Stars({ rating = 5 }) {
  return (
    <div className="flex text-amber-400">
      {Array.from({ length: rating }).map((_, i) => (
        <span key={i} className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
      ))}
    </div>
  );
}

function Attribution({ name, country }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-[#8fb3ff] flex items-center justify-center text-white font-bold border-2 border-[#00255a]/10">
        {name?.charAt(0)}
      </div>
      <div>
        <h4 className="font-semibold text-[#00255a]">{name}</h4>
        {country && <p className="text-[12px] text-[#44474f]">{country}</p>}
      </div>
    </div>
  );
}
